import { Assignment, HighlightOff, Security } from '@mui/icons-material';
import {
  Paper,
  Typography,
  FormControl,
  CardContent,
  Button,
  Container,
  Grid,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import { QrReader } from 'react-qr-reader';
import LoadingButton from '../../@iosper/components/LoadingButton';
import { useForm } from 'react-hook-form';
import { useAfiliados } from '../Afiliados/AfiliadosProvider';
import AfiliadoAutocomplete from '../Afiliados/AfiliadoAutocomplete';
import {
  validarTokenConsulta,
  validarOrdenDigitalConsulta,
  autorizarPrescripciones,
  // HARDCODE TEMPORAL: importado como reemplazo del WebSocket. Ver comentario en OrdenDigital.js.
  consultarEstadoTramite,
} from '../../services/Seo/OrdenDigital';
import OrdenValidadaResumidaForm from './OrdenValidadaResumidaForm';
import OrdenPreview from './OrdenPreview';
import { Redirect, useLocation } from 'react-router-dom';
import { Box } from '@mui/system';
import ReplayIcon from '@mui/icons-material/Replay';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ReactComponent as WarningImg } from '../../assets/images/svg/mobile-warning.svg';
import {
  sendMessageOrdenCancelada,
  // HARDCODE TEMPORAL: createWebSocketConnection deshabilitado porque nginx en producción
  // bloquea las conexiones WebSocket desde el browser de mi-iosper. Se reemplaza con
  // polling HTTP (startPolling / consultarEstadoTramite). Restaurar cuando nginx se corrija.
  // createWebSocketConnection,
} from '../../services/WebSocket/WebSocket';
import { useTheme } from '@emotion/react';
import { LectorQRGenerarOrdenDigitalMobile } from './components/LectorQRGenerarOrdenDigitalMobile';

const TIPO_TOKEN_CONSULTA = '01';
const TIPO_TOKEN_PRACTICA = '09';

const WS_STATE_SIN_PRESCRIPCIONES = 'SinPrescripcionesElectronicas';

const GenerarOrdenDigital = (props) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedAfiliado, setSelectedAfiliado] = useState(null);
  // Dialog Orden
  const [open, setOpen] = useState(false);
  const [datosOrdenReceived, setDatosOrdenReceived] = useState({});
  const [loadingOrden, setLoadingOrden] = useState(false);
  const [showSelectedAfiliado, setShowSelectedAfiliado] = useState(false);
  const [showScanner, setShowScanner] = useState(!location?.state?.qrToken);
  const [openDialogReporte, setOpenDialogReporte] = useState(false);
  const [urlReporte, setUrlReporte] = useState('');
  const [datosOrdenGenerada, setDatosOrdenGenerada] = useState('');
  const [onCancelar, setOnCancelar] = useState(false);
  const [qrNoValido, setQrNoValido] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showBtnHelp, setShowBtnHelp] = useState(false);
  const [dataToken, setDataToken] = useState(null);
  const dataTokenRef = useRef(null);

  // Estados para flujo práctica electrónica
  const [tipoToken, setTipoToken] = useState(null);
  const [showAutorizacion, setShowAutorizacion] = useState(false);
  const [showEsperandoPrescripcion, setShowEsperandoPrescripcion] = useState(false);
  const [showSinPrescripciones, setShowSinPrescripciones] = useState(false);
  const [loadingAutorizar, setLoadingAutorizar] = useState(false);
  const [showErrorPrescripcion, setShowErrorPrescripcion] = useState(false);
  const [errorPrescripcionMensaje, setErrorPrescripcionMensaje] = useState('');
  const [showPrestadorCancelo, setShowPrestadorCancelo] = useState(false);
  // HARDCODE TEMPORAL: era wsRef (WebSocket), ahora guarda el ID del intervalo de polling.
  // Restaurar a wsRef con WebSocket cuando nginx permita conexiones WSS desde el browser.
  const pollingIntervalRef = useRef(null);
  const ordenConfirmadaRef = useRef(false);

  const { afiliados, loading: afiliadosLoading } = useAfiliados();
  const pendingTokenDataRef = useRef(null);
  const pendingTipoTokenRef = useRef(null);

  // HARDCODE TEMPORAL: cleanup del polling al desmontar.
  // Originalmente cerraba el WebSocket. Ahora limpia el intervalo de polling.
  // Restaurar cierre de WebSocket cuando nginx se corrija.
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setShowBtnHelp(true);
    }, 180000); //180000 milisegundos son 3 munitos
  }, [showBtnHelp]);

  useEffect(() => {
    if (location?.state?.tokenData) {
      // Datos ya validados por SmartQrReader — no repetir llamada API
      const data = location.state.tokenData;
      setDataToken(data);
      dataTokenRef.current = data;
      const tipo = data.tipoToken || TIPO_TOKEN_CONSULTA;
      setTipoToken(tipo);
      if (afiliadosLoading) {
        pendingTokenDataRef.current = data;
        pendingTipoTokenRef.current = tipo;
      } else {
        procesarSeleccionAfiliado(data, tipo, afiliados);
      }
    } else if (location?.state?.qrToken) {
      validarToken(location.state.qrToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // HARDCODE TEMPORAL: era closeWebSocket() que cerraba la conexión WebSocket.
  // Ahora detiene el intervalo de polling. Mismo nombre para no cambiar todos los call sites.
  // Restaurar implementación original cuando nginx permita WSS desde el browser.
  const closeWebSocket = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // HARDCODE TEMPORAL: era handleWebSocketMessage() que procesaba eventos WebSocket (event.data).
  // Ahora recibe directamente el objeto de estado que devuelve el endpoint de polling.
  // La lógica de estados es idéntica; solo cambia la fuente del dato.
  // Restaurar manejo de eventos WebSocket cuando nginx se corrija.
  const handleEstadoTramite = useCallback((estado) => {
    if (!estado) return;
    const state = estado.state;

    switch (state) {
      case WS_STATE_SIN_PRESCRIPCIONES:
        closeWebSocket();
        setShowEsperandoPrescripcion(false);
        setShowAutorizacion(false);
        setShowSinPrescripciones(true);
        break;
      case 'PrescripcionSeleccionada':
        closeWebSocket();
        setShowEsperandoPrescripcion(false);
        setDatosOrdenReceived(estado.data);
        setOpen(true);
        break;
      case 'OrdenCancelada':
        closeWebSocket();
        setShowScanner(false);
        setShowSelectedAfiliado(false);
        setShowAutorizacion(false);
        setShowEsperandoPrescripcion(false);
        setShowSinPrescripciones(false);
        setShowErrorPrescripcion(false);
        setShowPrestadorCancelo(true);
        break;
      case 'OrdenGenerada':
        break;
      case 'ErrorPrescripcionSeleccionada':
        closeWebSocket();
        setShowEsperandoPrescripcion(false);
        setShowAutorizacion(false);
        setErrorPrescripcionMensaje(
          estado.message || 'Ocurrió un error al procesar la prescripción seleccionada.',
        );
        setShowErrorPrescripcion(true);
        break;
      case 'ErrorOrden':
        closeWebSocket();
        setShowEsperandoPrescripcion(false);
        setShowAutorizacion(false);
        setErrorPrescripcionMensaje(
          estado.message || 'Ocurrió un error al generar la orden.',
        );
        setShowErrorPrescripcion(true);
        break;
      default:
        break;
    }
  }, [closeWebSocket]);

  // HARDCODE TEMPORAL: reemplaza connectWebSocket() con un mecanismo de polling HTTP.
  // Motivo: nginx en producción bloquea conexiones WebSocket desde el browser de mi-iosper.
  // Intervalo: 3 segundos. Timeout: 4.5 minutos (antes del TTL de Redis de 5 min).
  // Restaurar connectWebSocket() cuando nginx se corrija y eliminar esta función.
  const startPolling = useCallback((token) => {
    closeWebSocket(); // limpia cualquier polling previo

    // Timeout de seguridad: si después de 4.5 minutos no hubo respuesta, la sesión expiró
    const timeoutId = setTimeout(() => {
      if (pollingIntervalRef.current) {
        closeWebSocket();
        setShowEsperandoPrescripcion(false);
        setErrorPrescripcionMensaje('La sesión expiró. Por favor, intentá nuevamente.');
        setShowErrorPrescripcion(true);
      }
    }, 270000); // 270s = 4.5 min, antes del TTL de Redis (300s)

    const intervalId = setInterval(async () => {
      try {
        const estado = await consultarEstadoTramite(token);
        if (estado) {
          clearTimeout(timeoutId);
          handleEstadoTramite(estado);
        }
      } catch (e) {
        console.error('[GenerarOrdenDigital] ❌ Error al consultar estado tramite:', e);
      }
    }, 3000); // poll cada 3 segundos

    pollingIntervalRef.current = intervalId;
  }, [closeWebSocket, handleEstadoTramite]);

  const procesarSeleccionAfiliado = useCallback((data, tipo, afiliadosActuales) => {
    if (tipo === TIPO_TOKEN_PRACTICA) {
      if (afiliadosActuales?.length > 1) {
        setShowSelectedAfiliado(true);
      } else {
        setShowAutorizacion(true);
      }
    } else {
      if (afiliadosActuales?.length > 1) {
        setShowSelectedAfiliado(true);
      } else {
        validarOrdenDigital(data);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!afiliadosLoading && pendingTokenDataRef.current) {
      const data = pendingTokenDataRef.current;
      const tipo = pendingTipoTokenRef.current;
      pendingTokenDataRef.current = null;
      pendingTipoTokenRef.current = null;
      procesarSeleccionAfiliado(data, tipo, afiliados);
    }
  }, [afiliadosLoading, afiliados, procesarSeleccionAfiliado]);

  const handleOnQRResult = (result, e) => {
    try {
      if (result) {
        setShowScanner(false);
        const res = result?.text;
        JSON.parse(res);
        validarToken(res);
      }
    } catch (e) {
      setQrNoValido(true);
    }
  };

  const validarToken = async (token) => {
    try {
      const data = await validarTokenConsulta(token);
      setDataToken(data);
      dataTokenRef.current = data;
      setShowScanner(false);

      const tipo = data.tipoToken || TIPO_TOKEN_CONSULTA;
      setTipoToken(tipo);

      // HARDCODE TEMPORAL: se eliminó connectWebSocket() aquí.
      // Antes se conectaba el WebSocket al escanear el QR. Ahora el polling
      // se inicia más tarde, en handleAutorizar(), una vez que el afiliado autoriza.
      // Restaurar connectWebSocket(data.token) aquí cuando nginx se corrija.

      if (afiliadosLoading) {
        // Afiliados aún no cargados (primer login): diferir hasta que estén disponibles
        pendingTokenDataRef.current = data;
        pendingTipoTokenRef.current = tipo;
      } else {
        procesarSeleccionAfiliado(data, tipo, afiliados);
      }
    } catch (e) {
      setShowScanner(true);
    }
  };

  const validarOrdenDigital = async (data) => {
    try {
      const res = await validarOrdenDigitalConsulta(getDataOrden(data));
      setDatosOrdenReceived(res);
      setOpen(!open);
      setLoadingOrden(false);
    } catch (e) {
      setLoadingOrden(false);
      setShowScanner(true);
      setShowSelectedAfiliado(false);
    }
  };

  const getDataOrden = (dataToken) => {
    let data = {
      numeroTarjeta:
        afiliados?.length === 1
          ? afiliados[0].tarjeta.numeroTarjeta
          : selectedAfiliado.tarjeta.numeroTarjeta,
      codigoPrestador: dataToken.codigoPrestador,
      tipoPrestador: dataToken.tipoPrestador,
      token: dataToken.token,
      nombreUsuario: dataToken.nombreUsuario,
      femer: dataToken.femer,
      codigoPractica: dataToken.codigoPractica,
    };
    return data;
  };

  const getIdAfiliado = () => {
    return afiliados?.length === 1
      ? afiliados[0].afiliado.idAfiliado
      : selectedAfiliado?.afiliado?.idAfiliado;
  };

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'onBlur' });

  const onSubmit = (data, event) => {
    setLoadingOrden(true);
    event.preventDefault();

    if (tipoToken === TIPO_TOKEN_PRACTICA) {
      // En flujo práctica, después de seleccionar afiliado se muestra autorización
      setShowSelectedAfiliado(false);
      setShowAutorizacion(true);
      setLoadingOrden(false);
    } else {
      validarOrdenDigital(dataToken);
    }
  };

  const validateAfiliado = () => {
    return selectedAfiliado == null ? false : true;
  };

  const handleSelectAfiliado = async (afiliado) => {
    setSelectedAfiliado(afiliado);
  };

  // HARDCODE TEMPORAL: antes solo llamaba autorizarPrescripciones() y esperaba el resultado
  // via WebSocket. Ahora usa la respuesta HTTP directamente para detectar SinPrescripciones,
  // e inicia el polling para esperar PrescripcionSeleccionada / OrdenCancelada.
  // Restaurar comportamiento original (sin chequeo de res ni startPolling) cuando nginx se corrija.
  const handleAutorizar = async () => {
    setLoadingAutorizar(true);
    try {
      const idAfiliado = getIdAfiliado();
      const res = await autorizarPrescripciones({
        token: dataTokenRef.current.token,
        idAfiliado: idAfiliado,
      });
      setLoadingAutorizar(false);
      setShowAutorizacion(false);

      if (!res?.prescripciones || res.prescripciones.length === 0) {
        // Sin prescripciones: resultado inmediato desde HTTP, no se necesita polling
        setShowSinPrescripciones(true);
      } else {
        // Hay prescripciones: el prestador debe seleccionar una en mi.consultorio.
        // HARDCODE TEMPORAL: inicia polling en lugar de escuchar WebSocket.
        setShowEsperandoPrescripcion(true);
        startPolling(dataTokenRef.current.token);
      }
    } catch (e) {
      setLoadingAutorizar(false);
    }
  };

  const handleCancelarPractica = () => {
    enviarMensajeCancelada();
    closeWebSocket();
    setOnCancelar(true);
  };

  const handlerOrdenConfirmada = (datosOrden, urlPdf) => {
    ordenConfirmadaRef.current = true;
    setUrlReporte(urlPdf);
    setDatosOrdenGenerada(datosOrden);
    setOpenDialogReporte(true);
    closeWebSocket();
  };

  const handleClose = () => {
    setOnCancelar(true);
  };

  const handleReplyFromQrNoValido = () => {
    setQrNoValido(false);
  };

  const handleHelp = () => {
    setShowHelp(true);
  };

  const handleReplyFromHelp = () => {
    setShowHelp(false);
    setShowBtnHelp(false);
  };

  const handleCloseOrdenValidadaResumidaForm = () => {
    setOpen(false);
    if (tipoToken === TIPO_TOKEN_PRACTICA) {
      if (!ordenConfirmadaRef.current) {
        // Solo cancela si el afiliado cerró sin confirmar la orden
        handleCancelarPractica();
      }
    } else {
      setShowScanner(true);
      setShowSelectedAfiliado(false);
    }
  };

  const handlerCerrarPreview = () => {
    setOpenDialogReporte(false);
    closeWebSocket();
    setOnCancelar(true);
  };

  const enviarMensajeCancelada = async () => {
    const token = dataTokenRef.current?.token || dataToken?.token;
    if (!token) return;
    try {
      await sendMessageOrdenCancelada(token);
    } catch (e) {
      console.log('Error al enviar mensaje de orden cancelada:', e);
    }
  };

  const handlerOrdenCancelada = () => {
    enviarMensajeCancelada();
    if (tipoToken === TIPO_TOKEN_PRACTICA) {
      closeWebSocket();
    }
  };

  const contentHelp = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item>
          <Grid container direction='row' spacing={1}>
            <Grid item>
              <HelpOutlineIcon color='primary' />
            </Grid>
            <Grid item>
              <Typography variant='subtitle2'>¿Querés generar una Orden Electrónica?</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            Apuntá la cámara hacia el código QR que generó el prestador.
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            Una vez leído correctamente el código, seguí las intrucciones en pantalla para completar
            el proceso.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<ReplayIcon />}
            variant='contained'
            color='primary'
            fullWidth={true}
            onClick={(e) => handleReplyFromHelp(e)}
          >
            Reintentar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentQrNoValido = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item>
          <Grid container direction='row' justifyContent='center' alignItems='center'>
            <Grid xs={2} item>
              <WarningImg />
            </Grid>
            <Grid xs={10} item>
              <Typography align='center' variant='body2' color='secondary'>
                Oops! No pudimos escanear correctamente el código QR
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant='body2'>Verificá que tu cámara esté alineada.</Typography>
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            Verificá que la cámara de tu dispositivo esté limpia.
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            Verificá que el reflejo de las luces no genere ruidos en la foto.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<ReplayIcon />}
            variant='contained'
            color='primary'
            fullWidth={true}
            onClick={(e) => handleReplyFromQrNoValido(e)}
          >
            Reintentar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentSeleccionarAfiliado = (
    <Paper sx={{ p: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent>
          <FormControl fullWidth={true} variant='outlined' margin='normal'>
            <AfiliadoAutocomplete
              ref={register('afiliadoRegister', {
                validate: validateAfiliado,
              })}
              onSelectAfiliado={handleSelectAfiliado}
            />
            {errors.afiliadoRegister && (
              <p style={{ color: 'red' }}>Debe seleccionar el afiliado</p>
            )}
          </FormControl>
          <LoadingButton
            fullWidth={true}
            variant='contained'
            size='medium'
            color='primary'
            startIcon={<Assignment />}
            type='submit'
            loading={loadingOrden ? 'show' : 'hide'}
            content={'Generar'}
          />
        </CardContent>
      </form>
    </Paper>
  );

  const contentAutorizacion = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <Typography variant='subtitle1' align='center' sx={{ fontWeight: 'bold' }}>
            Autorización de acceso
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2' align='center'>
            El prestador <strong>{dataTokenRef.current?.nombre}</strong> ({dataTokenRef.current?.nombreUsuario}) solicita acceso a
            tus prescripciones electrónicas vigentes.
          </Typography>
        </Grid>        
        <Grid item xs={12}>
          <LoadingButton
            fullWidth={true}
            variant='contained'
            size='medium'
            color='primary'
            startIcon={<Security />}
            onClick={handleAutorizar}
            loading={loadingAutorizar ? 'show' : 'hide'}
            content={'Autorizar'}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<HighlightOff />}
            variant='contained'
            color='neutral'
            fullWidth={true}
            onClick={handleCancelarPractica}
          >
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentEsperandoPrescripcion = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <Typography variant='subtitle1' align='center' sx={{ fontWeight: 'bold' }}>
            Esperando al prestador
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2' align='center'>
            Esperando que el prestador seleccione la prescripción electrónica...
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<HighlightOff />}
            variant='contained'
            color='neutral'
            fullWidth={true}
            onClick={handleCancelarPractica}
          >
            Cancelar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentSinPrescripciones = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <Typography variant='subtitle1' align='center' sx={{ fontWeight: 'bold' }}>
            Sin prescripciones vigentes
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2' align='center'>
            No tenés prescripciones electrónicas vigentes para este prestador.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<HighlightOff />}
            variant='contained'
            color='neutral'
            fullWidth={true}
            onClick={() => setOnCancelar(true)}
          >
            Volver
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentErrorPrescripcion = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <Grid container direction='row' justifyContent='center' alignItems='center'>
            <Grid xs={2} item>
              <WarningImg />
            </Grid>
            <Grid xs={10} item>
              <Typography align='center' variant='subtitle1' sx={{ fontWeight: 'bold' }} color='secondary'>
                Error al procesar la prescripción
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2' align='center'>
            {errorPrescripcionMensaje}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<ReplayIcon />}
            variant='contained'
            color='primary'
            fullWidth={true}
            onClick={() => {
              setShowErrorPrescripcion(false);
              setShowScanner(true);
            }}
          >
            Reintentar
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentPrestadorCancelo = (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={3} justifyContent='center' alignItems='center'>
        <Grid item xs={12}>
          <Grid container direction='row' justifyContent='center' alignItems='center'>
            <Grid xs={2} item>
              <WarningImg />
            </Grid>
            <Grid xs={10} item>
              <Typography align='center' variant='subtitle1' sx={{ fontWeight: 'bold' }} color='secondary'>
                Atención
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2' align='center'>
            El prestador canceló la operación.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            size='small'
            startIcon={<HighlightOff />}
            variant='contained'
            color='neutral'
            fullWidth={true}
            onClick={() => setOnCancelar(true)}
          >
            Volver
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const contentScaner = () => {
    if (isMobileScreen) {
      return (
        <LectorQRGenerarOrdenDigitalMobile
          onResult={handleOnQRResult}
          title='Orden Electrónica'
          description='Escaneá el código QR generado por el médico prestador en la aplicación <b>Mi Consultorio</b>.' />
      );
    }
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant='string' component={'div'} align='center' sx={{ pb: 3 }}>
          Escanea el código QR generado por médico prestador en la aplicación miconsultorio
        </Typography>
        <QrReader
          constraints={{ facingMode: 'environment' }}
          onResult={handleOnQRResult}
          style={{ width: '100%' }}
        />
        <Box sx={{ p: 2 }}>
          <Button
            size='small'
            variant='text'
            color='primary'
            fullWidth={true}
            onClick={(e) => handleHelp(e)}
          >
            {' '}
            ¿Necesitás ayuda?{' '}
          </Button>
          <Button
            size='small'
            startIcon={<HighlightOff />}
            variant='contained'
            color='neutral'
            fullWidth={true}
            onClick={(e) => handleClose(e)}
          >
            Cancelar
          </Button>
        </Box>
      </Paper>
    )
  };

  return onCancelar ? (
    <Redirect
      to={{
        pathname: '/',
        state: { from: '/read-orden' },
      }}
    />
  ) : qrNoValido ? (
    contentQrNoValido
  ) : showHelp ? (
    contentHelp
  ) : (
    <Container component='main' maxWidth='sm'>
      {showScanner ? contentScaner() : null}
      {showSelectedAfiliado ? contentSeleccionarAfiliado : null}
      {showAutorizacion ? contentAutorizacion : null}
      {showEsperandoPrescripcion ? contentEsperandoPrescripcion : null}
      {showSinPrescripciones ? contentSinPrescripciones : null}
      {showErrorPrescripcion ? contentErrorPrescripcion : null}
      {showPrestadorCancelo ? contentPrestadorCancelo : null}

      <OrdenValidadaResumidaForm
        open={open}
        setOpen={handleCloseOrdenValidadaResumidaForm}
        datosOrden={datosOrdenReceived}
        handlerOrdenConfirmada={handlerOrdenConfirmada}
        handlerOrdenCancelada={handlerOrdenCancelada}
      ></OrdenValidadaResumidaForm>

      <OrdenPreview
        open={openDialogReporte}
        setOpen={setOpenDialogReporte}
        urlReporte={urlReporte}
        datosOrden={datosOrdenGenerada}
        nombreReporte={'Orden_oser_' + datosOrdenGenerada.numeroOrden}
        handlerCerrarPreview={handlerCerrarPreview}
        showDownload={false}
        showPrint={false}
      ></OrdenPreview>
    </Container>
  );
};

export default GenerarOrdenDigital;
